import offer from '../config/offer';
import { useCountry } from './geo';

/**
 * La page anglaise affiche et encaisse des dollars. La page française, des euros.
 * Exception : au Canada (pays Cloudflare, lib/geo.js), dollars canadiens dans les deux langues.
 * Le Worker applique la même règle au checkout (checkoutPricing dans netlify-proxy/worker.js).
 */

function currencyForLang(lang) {
  return lang === 'en' ? 'usd' : 'eur';
}

/** 49 → « 49 € » (fr, EUR), « €49 » (en, EUR), « $49 » (en, USD), « 49 $ » (fr, USD). Centimes seulement s'il y en a. */
export function formatMoney(amount, currency, lang) {
  const cents = Math.round(Number(amount) * 100) % 100 !== 0;
  const text = new Intl.NumberFormat(lang === 'en' ? 'en-US' : 'fr-FR', {
    style: 'currency',
    currency: currency.toUpperCase(),
    currencyDisplay: 'narrowSymbol',
    minimumFractionDigits: cents ? 2 : 0,
    maximumFractionDigits: cents ? 2 : 0,
  }).format(amount);
  return text.replace(/ /g, ' ');
}

/**
 * @param {'fr' | 'en'} lang
 * @returns {{ currency: 'eur' | 'usd' | 'cad', amount: number, perDay: number, money: (n: number) => string, previous: number | null,
 *   endsAt: number | null, next: number | null }}
 */
export function usePricing(lang) {
  const country = useCountry();
  const currency = country === 'CA' ? 'cad' : currencyForLang(lang);
  // Prix de promotion jusqu'à offer.launchEndsAt, puis prix normal (même date que le Worker).
  // launchEndsAt null : promotion sans date de fin (barré sans compte à rebours). Le barré n'existe qu'en euros (99 €).
  const launchEnd = Date.parse(offer.launchEndsAt || '');
  const hasEnd = Number.isFinite(launchEnd);
  const afterLaunch = hasEnd && Date.now() >= launchEnd;
  const launchOn = currency === 'eur' && !afterLaunch && Number(offer.originalPrice) > Number(offer.price);
  const now = { eur: offer.price, usd: offer.usdPrice, cad: offer.cadPrice };
  const later = { eur: offer.originalPrice, usd: offer.usdPriceAfter, cad: offer.cadPriceAfter };
  const amount = Number(afterLaunch && Number(later[currency]) > 0 ? later[currency] : now[currency]);
  const previous = launchOn ? Number(offer.originalPrice) : null;
  // Arrondi en dessous : 49 au lieu de 99 = « −50 % », jamais plus que la vraie remise.
  const discount = previous !== null ? Math.floor(((previous - amount) / previous) * 100) : null;
  // « Moins de X par jour » : arrondi au-dessus, pour rester vrai (29 → 1, 39 → 2, 49 → 2).
  const perDay = Math.ceil(amount / 30);
  // Offre en cours, toutes devises : fin réelle et prix d'après (V70 : bandeau, comptes à rebours). Pas de barré en dollars.
  const dealOn = hasEnd && !afterLaunch && Number(later[currency]) > amount;
  return {
    currency, amount, previous, discount, perDay,
    launchEndsAt: launchOn && hasEnd ? launchEnd : null,
    endsAt: dealOn ? launchEnd : null,
    next: dealOn ? Number(later[currency]) : null,
    money: (n) => formatMoney(n, currency, lang),
  };
}
